import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Stack,
  Tabs,
  Tab,
  Button,
  Paper,
  Typography,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useApi } from '../api/client';
import { Page } from '../components/Page';
import HealthFilterBar from '../components/health/HealthFilterBar';
import HealthNewFileDialog from '../components/health/HealthNewFileDialog';
import HealthFilePreview from '../components/health/HealthFilePreview';
import HealthExploreView from '../components/health/HealthExploreView';
import HealthGroupedView from '../components/health/HealthGroupedView';
import HealthTimelineView from '../components/health/HealthTimelineView';
import { buildFilesQuery } from '../components/health/healthUtils';
import { attachmentsForDownload, downloadHealthFilesZip } from '../components/health/healthDownloadUtils';
import DownloadIcon from '@mui/icons-material/Download';

export default function Health() {
  const { request } = useApi();
  const requestRef = useRef(request);
  requestRef.current = request;

  const [cases, setCases] = useState([]);
  const [activeCaseId, setActiveCaseId] = useState(null);
  const [fieldOptions, setFieldOptions] = useState({ types: [], report_types: [], hospitals: [] });
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewTab, setViewTab] = useState('explore');
  const [searchQ, setSearchQ] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [reportTypeFilter, setReportTypeFilter] = useState('all');
  const [reloadTick, setReloadTick] = useState(0);

  const [addCaseOpen, setAddCaseOpen] = useState(false);
  const [newCaseName, setNewCaseName] = useState('');
  const [editCaseOpen, setEditCaseOpen] = useState(false);
  const [editCaseName, setEditCaseName] = useState('');
  const [deleteCaseConfirm, setDeleteCaseConfirm] = useState(false);
  const [newFileOpen, setNewFileOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [deleteFileId, setDeleteFileId] = useState(null);
  const [downloadMode, setDownloadMode] = useState(false);
  const [downloadSelected, setDownloadSelected] = useState({});
  const [downloading, setDownloading] = useState(false);

  const loadCases = useCallback(async () => {
    try {
      const res = await requestRef.current('/api/health/cases');
      if (res.ok) {
        const data = await res.json();
        const list = data.cases || [];
        setCases(list);
        if (list.length > 0) {
          setActiveCaseId((prev) => {
            if (prev && list.some((c) => c.id === prev)) return prev;
            return list[0].id;
          });
        } else {
          setActiveCaseId(null);
        }
      }
    } catch (e) {
      console.error('Cases load failed', e);
    }
  }, []);

  const loadFieldOptions = useCallback(async (caseId) => {
    if (!caseId) return;
    try {
      const res = await requestRef.current(`/api/health/cases/${caseId}/field-options`);
      if (res.ok) {
        const data = await res.json();
        setFieldOptions(data);
      }
    } catch (e) {
      console.error('Field options load failed', e);
    }
  }, []);

  const bumpReload = useCallback(() => {
    setReloadTick((t) => t + 1);
  }, []);

  useEffect(() => {
    loadCases();
  }, [loadCases]);

  useEffect(() => {
    if (activeCaseId) loadFieldOptions(activeCaseId);
  }, [activeCaseId, reloadTick, loadFieldOptions]);

  useEffect(() => {
    if (!activeCaseId) {
      setLoading(false);
      setFiles([]);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    const qs = buildFilesQuery({
      q: appliedSearch,
      from,
      to,
      report_type: reportTypeFilter,
    });
    requestRef.current(`/api/health/cases/${activeCaseId}/files${qs}`)
      .then((res) => {
        if (cancelled) return null;
        return res.ok ? res.json() : null;
      })
      .then((data) => {
        if (!cancelled && data) setFiles(data.files || []);
      })
      .catch((e) => console.error('Files load failed', e))
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [activeCaseId, appliedSearch, from, to, reportTypeFilter, reloadTick]);

  function handleCaseTabChange(_, value) {
    if (value === '__add__') {
      setNewCaseName('');
      setAddCaseOpen(true);
      return;
    }
    setActiveCaseId(value);
  }

  async function handleAddCase() {
    const name = newCaseName.trim();
    if (!name) return;
    const res = await requestRef.current('/api/health/cases', {
      method: 'POST',
      body: JSON.stringify({ name }),
    });
    if (res.ok) {
      const created = await res.json();
      setAddCaseOpen(false);
      setNewCaseName('');
      await loadCases();
      setActiveCaseId(created.id);
    }
  }

  async function handleEditCase() {
    const name = editCaseName.trim();
    if (!name || !activeCaseId) return;
    const res = await requestRef.current(`/api/health/cases/${activeCaseId}`, {
      method: 'PATCH',
      body: JSON.stringify({ name }),
    });
    if (res.ok) {
      setEditCaseOpen(false);
      loadCases();
    }
  }

  async function handleDeleteCase() {
    if (!activeCaseId) return;
    const res = await requestRef.current(`/api/health/cases/${activeCaseId}`, { method: 'DELETE' });
    if (res.ok) {
      setDeleteCaseConfirm(false);
      setActiveCaseId(null);
      loadCases();
    }
  }

  function handleSearch() {
    setAppliedSearch(searchQ);
  }

  function handleFileUpdate(updated) {
    setFiles((prev) => {
      const out = [];
      for (const f of prev) {
        out.push(f.id === updated.id ? updated : f);
      }
      return out;
    });
    if (activeCaseId) loadFieldOptions(activeCaseId);
  }

  async function handleFileDelete(id) {
    setDeleteFileId(id);
  }

  async function confirmDeleteFile() {
    if (!deleteFileId) return;
    const res = await requestRef.current(`/api/health/files/${deleteFileId}`, { method: 'DELETE' });
    if (res.ok) {
      setDeleteFileId(null);
      bumpReload();
    }
  }

  function exitDownloadMode() {
    setDownloadMode(false);
    setDownloadSelected({});
  }

  function toggleDownloadSelect(fileId) {
    setDownloadSelected((prev) => ({ ...prev, [fileId]: !prev[fileId] }));
  }

  function selectAllDownloadable() {
    const next = {};
    for (let i = 0; i < files.length; i += 1) {
      const file = files[i];
      if (attachmentsForDownload(file).length > 0) next[file.id] = true;
    }
    setDownloadSelected(next);
  }

  const downloadSelectedCount = (() => {
    let n = 0;
    for (let i = 0; i < files.length; i += 1) {
      if (downloadSelected[files[i].id]) n += 1;
    }
    return n;
  })();

  async function handleDownloadSelected() {
    const picked = [];
    for (let i = 0; i < files.length; i += 1) {
      const file = files[i];
      if (downloadSelected[file.id]) picked.push(file);
    }
    if (picked.length === 0) return;
    setDownloading(true);
    try {
      await downloadHealthFilesZip(picked, request);
      exitDownloadMode();
    } catch (e) {
      console.error('Download failed', e);
    } finally {
      setDownloading(false);
    }
  }

  const downloadViewProps = {
    downloadMode,
    downloadSelected,
    onToggleDownloadSelect: toggleDownloadSelect,
  };

  const activeCase = cases.find((c) => c.id === activeCaseId);

  return (
    <Page>
      <Stack spacing={2}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
          <Typography variant="h6">Health</Typography>
          {activeCaseId && (
            <Stack direction="row" spacing={1}>
              {downloadMode ? (
                <>
                  <Button variant="outlined" onClick={exitDownloadMode} disabled={downloading}>
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={downloading ? <CircularProgress size={18} color="inherit" /> : <DownloadIcon />}
                    onClick={handleDownloadSelected}
                    disabled={downloading || downloadSelectedCount === 0}
                  >
                    Download{downloadSelectedCount > 0 ? ` (${downloadSelectedCount})` : ''}
                  </Button>
                </>
              ) : (
                <>
                  <Button variant="outlined" startIcon={<DownloadIcon />} onClick={() => setDownloadMode(true)} disabled={loading || files.length === 0}>
                    Download files
                  </Button>
                  <Button variant="contained" startIcon={<AddIcon />} onClick={() => setNewFileOpen(true)}>
                    New file
                  </Button>
                </>
              )}
            </Stack>
          )}
        </Stack>

        <Box sx={{ borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center' }}>
          {cases.length > 0 ? (
            <Tabs
              value={activeCaseId}
              onChange={handleCaseTabChange}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ flex: 1, minWidth: 0 }}
            >
              {cases.map((c) => (
                <Tab key={c.id} value={c.id} label={c.name} />
              ))}
              <Tab value="__add__" icon={<AddIcon fontSize="small" />} iconPosition="start" label="Add" sx={{ minHeight: 48 }} />
            </Tabs>
          ) : (
            <Tabs value="__add__" onChange={handleCaseTabChange} sx={{ flex: 1 }}>
              <Tab value="__add__" icon={<AddIcon fontSize="small" />} iconPosition="start" label="Add case" />
            </Tabs>
          )}
          {activeCaseId && (
            <>
              <IconButton size="small" onClick={() => { setEditCaseName(activeCase?.name || ''); setEditCaseOpen(true); }} aria-label="Edit case">
                <EditOutlinedIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" color="error" onClick={() => setDeleteCaseConfirm(true)} aria-label="Delete case">
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </>
          )}
        </Box>

        {cases.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary" gutterBottom>No cases yet.</Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setNewCaseName(''); setAddCaseOpen(true); }}>
              Add case
            </Button>
          </Paper>
        ) : activeCaseId && (
          <>
            <HealthFilterBar
              searchQ={searchQ}
              onSearchQChange={setSearchQ}
              onSearch={handleSearch}
              from={from}
              to={to}
              onFromChange={setFrom}
              onToChange={setTo}
              onClearDates={() => { setFrom(''); setTo(''); }}
              reportTypeFilter={reportTypeFilter}
              onReportTypeFilterChange={setReportTypeFilter}
              reportTypes={fieldOptions.report_types || []}
              loading={loading}
              showReportTypeChips={viewTab === 'timeline'}
            />

            <Tabs value={viewTab} onChange={(_, v) => setViewTab(v)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tab value="explore" label="Explore" />
              <Tab value="grouped" label="Grouped" />
              <Tab value="timeline" label="Timeline" />
            </Tabs>

            {downloadMode && (
              <Paper variant="outlined" sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Select files to download
                </Typography>
                <Button size="small" onClick={selectAllDownloadable}>
                  Select all
                </Button>
              </Paper>
            )}

            {loading ? (
              <Paper sx={{ p: 4, textAlign: 'center' }}>
                <CircularProgress />
              </Paper>
            ) : viewTab === 'explore' ? (
              <HealthExploreView
                files={files}
                fieldOptions={fieldOptions}
                request={request}
                onFileUpdate={handleFileUpdate}
                onFileDelete={handleFileDelete}
                onPreview={setPreview}
                {...downloadViewProps}
              />
            ) : viewTab === 'grouped' ? (
              <HealthGroupedView
                files={files}
                fieldOptions={fieldOptions}
                request={request}
                onFileUpdate={handleFileUpdate}
                onFileDelete={handleFileDelete}
                onPreview={setPreview}
                {...downloadViewProps}
              />
            ) : (
              <HealthTimelineView
                files={files}
                fieldOptions={fieldOptions}
                request={request}
                onFileUpdate={handleFileUpdate}
                onFileDelete={handleFileDelete}
                onPreview={setPreview}
                {...downloadViewProps}
              />
            )}
          </>
        )}
      </Stack>

      <HealthFilePreview preview={preview} onClose={() => setPreview(null)} request={request} />

      <HealthNewFileDialog
        open={newFileOpen}
        onClose={() => setNewFileOpen(false)}
        onSaved={bumpReload}
        request={request}
        caseId={activeCaseId}
      />

      <Dialog open={addCaseOpen} onClose={() => setAddCaseOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add case</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Case name"
            value={newCaseName}
            onChange={(e) => setNewCaseName(e.target.value)}
            sx={{ mt: 1 }}
            onKeyDown={(e) => e.key === 'Enter' && handleAddCase()}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddCaseOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAddCase} disabled={!newCaseName.trim()}>Add</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={editCaseOpen} onClose={() => setEditCaseOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Edit case</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Case name"
            value={editCaseName}
            onChange={(e) => setEditCaseName(e.target.value)}
            sx={{ mt: 1 }}
            onKeyDown={(e) => e.key === 'Enter' && handleEditCase()}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditCaseOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleEditCase} disabled={!editCaseName.trim()}>Save</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteCaseConfirm} onClose={() => setDeleteCaseConfirm(false)}>
        <DialogTitle>Delete case?</DialogTitle>
        <DialogContent>
          <Typography>This will delete the case and all its files. This cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteCaseConfirm(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDeleteCase}>Delete</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!deleteFileId} onClose={() => setDeleteFileId(null)}>
        <DialogTitle>Delete file?</DialogTitle>
        <DialogContent>
          <Typography>This will remove the file record and any uploaded attachment.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteFileId(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={confirmDeleteFile}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Page>
  );
}
